import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, ArrowUpRight } from "lucide-react";
import {
  PORTFOLIO,
  PORTFOLIO_FILTERS,
  driveThumb,
  cloudinaryPoster,
} from "@/lib/lumiqoData";
import MediaLightbox from "@/components/lumiqo/MediaLightbox";

const PortfolioCard = ({ item, i, onOpen }) => {
  const hasMedia = item.type === "video" && (item.src || item.driveId);
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 22 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.5, delay: i * 0.04 }}
      className="group relative rounded-3xl overflow-hidden bg-white border border-[#0a1d4d]/10 lq-shadow-soft hover:lq-shadow-glow transition-all duration-300"
      data-testid={`portfolio-card-${i}`}
    >
      <button
        type="button"
        onClick={() => hasMedia && onOpen(item)}
        disabled={!hasMedia}
        className="block w-full text-left"
        data-testid={`portfolio-card-button-${i}`}
      >
        <div
          className={`relative aspect-[9/14] overflow-hidden ${
            hasMedia
              ? "bg-[#0a1d4d]"
              : `bg-gradient-to-br ${item.accent || "from-[#0a1d4d] to-[#1d72ff]"}`
          }`}
        >
          {hasMedia ? (
            <img
              src={
                item.src
                  ? cloudinaryPoster(item.src)
                  : driveThumb(item.driveId, 800)
              }
              alt={item.title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <>
              <div className="absolute inset-0 lq-noise opacity-25" />
              <div className="absolute inset-0 lq-grid-bg-dark opacity-30 [mask-image:radial-gradient(circle_at_center,black,transparent_70%)]" />
            </>
          )}

          {/* Dark gradient overlay on hover for legibility */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent opacity-90" />

          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-white/95 backdrop-blur flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-xl">
              <Play className="w-6 h-6 text-[#0a1d4d] fill-current ml-1" />
            </div>
          </div>

          <div className="absolute top-4 left-4 px-2.5 py-1 rounded-full bg-white/90 text-[10px] font-semibold uppercase tracking-wide text-[#0a1d4d]">
            {item.category}
          </div>
          {!hasMedia && (
            <div className="absolute bottom-4 right-4 px-2.5 py-1 rounded-full bg-black/45 backdrop-blur text-[10px] font-semibold uppercase tracking-wide text-white">
              Video Coming Soon
            </div>
          )}
        </div>
        <div className="p-5 flex items-center justify-between">
          <h3 className="font-display font-semibold text-[#0a1d4d] text-base sm:text-lg tracking-tight">
            {item.title}
          </h3>
          <span
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#1d72ff] group-hover:text-[#0a1d4d] transition-colors"
            data-testid={`portfolio-view-${i}`}
          >
            View
            <ArrowUpRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </button>
    </motion.article>
  );
};

const Portfolio = () => {
  const [active, setActive] = useState("All");
  const [open, setOpen] = useState(false);
  const [media, setMedia] = useState(null);

  const filtered = useMemo(
    () =>
      active === "All"
        ? PORTFOLIO
        : PORTFOLIO.filter((p) => p.category === active),
    [active]
  );

  const openVideo = (item) => {
    setMedia({
      kind: "video",
      src: item.src,
      driveId: item.driveId,
      title: `${item.title} · ${item.category}`,
      aspect: "9/16",
    });
    setOpen(true);
  };

  return (
    <section
      id="work"
      className="relative py-24 sm:py-32 bg-white"
      data-testid="portfolio-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <div className="inline-block px-3 py-1 rounded-full bg-[#1d72ff]/10 text-[#1d72ff] text-xs font-semibold tracking-wide uppercase">
              Our Work
            </div>
            <h2
              className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#0a1d4d] mt-5 leading-[1.05]"
              data-testid="portfolio-heading"
            >
              Recent cuts &<br />
              viral moments.
            </h2>
          </div>

          <div className="flex flex-wrap gap-2">
            {PORTFOLIO_FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setActive(f)}
                className={`px-4 h-10 rounded-full text-sm font-semibold transition-all duration-200 ${
                  active === f
                    ? "bg-[#0a1d4d] text-white"
                    : "bg-[#f5f6fa] text-[#0a1d4d]/70 hover:bg-[#0a1d4d]/10"
                }`}
                data-testid={`portfolio-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <motion.div
          layout
          className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6"
        >
          <AnimatePresence mode="popLayout">
            {filtered.map((item, i) => (
              <PortfolioCard
                key={`${item.title}-${i}`}
                item={item}
                i={i}
                onOpen={openVideo}
              />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <MediaLightbox open={open} onOpenChange={setOpen} media={media} />
    </section>
  );
};

export default Portfolio;
